import { ensureMapEntry } from "../../utils";
import { operationsByPermission, operationsByPermissionObject, permissionsByObjectByKind, rolesPermissionsByRoleByObjectByKind } from "./utilities";

/**
 * @implements {IPermissionMatrix}
 */
export class PermissionMatrix
{
    /**
     * @param {PermissionMatrixEntities} entities
     */
    constructor(entities)
    {
        this.operations = new Map(Array.from(entities.operations).map(entity => [entity.id, entity]));
        this.permissions = new Map(Array.from(entities.permissions).map(entity => [entity.id, entity]));
        this.roles = Array.from(entities.roles);
        this.rolesPermissionsByRoleByObjectByKind = rolesPermissionsByRoleByObjectByKind(entities.rolesPermissions, this.permissions);
        this.permissionObjectsGroups = new Map([...entities.permissionObjectsGroups].map(entity => [entity.id, entity]));
        this.permissionObjects = new Map(Array.from(entities.permissionObjects).map(entity => [entity.id, { ...entity, name: entity.name }]));
        this.permissionsByObjectByKind = permissionsByObjectByKind(entities.permissions);
        this.operationsByPermission = operationsByPermission(entities.operations);
        this.operationsByPermissionObject = operationsByPermissionObject(entities.operations, this.permissions);
        /** @type {Map<number, Map<number, boolean>>} */
        this.modifications = new Map();
    }

    /**
     * @param {number} permissionObjectId
     * @param {number} permissionKind
     * @returns {boolean}
     */
    hasPermissionKind(permissionObjectId, permissionKind)
    {
        return this.permissionsByObjectByKind.get(permissionObjectId)?.has(permissionKind) ?? false;
    }

    /**
     * @param {number} roleId
     * @param {number} permissionObjectId
     * @param {number} permissionKind
     * @returns {boolean}
     */
    has(roleId, permissionObjectId, permissionKind)
    {
        const permission = this.permissionsByObjectByKind.get(permissionObjectId)?.get(permissionKind);
        if (!permission) { throw new Error(`Permission for object ${permissionObjectId} and kind ${permissionKind} not found`); }
        const modification = this.modifications.get(roleId)?.get(permission.id);
        // eslint-disable-next-line no-undefined
        if (modification !== undefined) { return modification; }
        return this.rolesPermissionsByRoleByObjectByKind.get(roleId)?.get(permissionObjectId)?.has(permissionKind) ?? false;
    }

    /**
     * @param {number} roleId
     * @param {number} permissionObjectId
     * @param {number} permissionKind
     * @param {boolean} allowed
     */
    set(roleId, permissionObjectId, permissionKind, allowed)
    {
        const permission = this.permissionsByObjectByKind.get(permissionObjectId)?.get(permissionKind);
        if (!permission) { throw new Error(`Permission for object ${permissionObjectId} and kind ${permissionKind} not found`); }
        const originalRolePermission = this.rolesPermissionsByRoleByObjectByKind.get(roleId)?.get(permissionObjectId)?.get(permissionKind);
        const modification = this.modifications.get(roleId)?.get(permission.id);

        // eslint-disable-next-line no-undefined
        if (modification === undefined)
        {
            // eslint-disable-next-line no-undefined
            const originalAllowed = originalRolePermission === undefined ? false : originalRolePermission.allowed;
            if (originalAllowed !== allowed)
            {
                const roleModifications = ensureMapEntry(this.modifications, roleId, () => new Map());
                roleModifications.set(permission.id, allowed);
                return true;
            }
            return false;
        }
        else
        {
            if (modification === allowed) { return false; }
            const roleModifications = this.modifications.get(roleId);
            if (!roleModifications) { throw new Error('Invariant violation: role modifications map not found'); }
            roleModifications.delete(permission.id);
            if (roleModifications.size === 0) { this.modifications.delete(roleId); }
            return true;
        }
    }

    /**
     * @param {number} roleId
     * @param {number} permissionObjectId
     * @param {number} permissionKind
     * @returns {boolean}
     */
    isDirty(roleId, permissionObjectId, permissionKind)
    {
        const permission = this.permissionsByObjectByKind.get(permissionObjectId)?.get(permissionKind);
        if (!permission) { throw new Error(`Permission for object ${permissionObjectId} and kind ${permissionKind} not found`); }
        // eslint-disable-next-line no-undefined
        return this.modifications.get(roleId)?.get(permission.id) !== undefined;
    }

    /**
     * @param {number} permissionObjectId
     * @param {number} permissionKind
     * @returns {OperationEntity[]}
     */
    getOperations(permissionObjectId, permissionKind)
    {
        const permission = this.permissionsByObjectByKind.get(permissionObjectId)?.get(permissionKind);
        if (!permission) { return []; }
        return this.operationsByPermission.get(permission.id) ?? [];
    }

    /**
     * @param {number} permissionObjectId
     * @returns {OperationEntity[]}
     */
    getOperationsForPermissionObject(permissionObjectId)
    {
        return this.operationsByPermissionObject.get(permissionObjectId) ?? [];
    }
}